import { gql, useMutation } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from 'components/Button';
import { FormInput, FormPanel } from 'components/FormPanel';

type Designation = {
  id: string;
  name: string;
  employeeCount: number;
  createdAt: string;
};

type CreateDesignationMutationResponse = {
  createDesignation: Designation;
};

type CreateDesignationMutationVariables = {
  name: string;
  departmentId: string;
};

const CREATE_DESIGNATION_MUTATION = gql`
  mutation CreateDesignation($name: String!, $departmentId: ID!) {
    createDesignation(name: $name, departmentId: $departmentId) {
      id
      name
      employeeCount
      createdAt
    }
  }
`;

const CreateDesignationDialogButton: FC = () => {
  const [showDialog, toggleDialog] = useState(false);
  const { departmentId } = useParams<{ departmentId: string }>() as { departmentId: string };

  const [createDesignation, { loading, error }] = useMutation(CREATE_DESIGNATION_MUTATION, {
    update(cache, { data }) {
      if (!data) return;
      cache.modify({
        id: `DepartmentType:${departmentId}`,
        fields: {
          designations(existingDesignationsRef) {
            const newDesignationNodeRef = cache.writeFragment({
              data: data.createDesignation,
              fragment: gql`
                fragment NewDesignation on DesignationType {
                  id
                  name
                  employeeCount
                  createdAt
                }
              `,
            });
            const updatedDesignationsRef = { ...existingDesignationsRef };
            updatedDesignationsRef.nodes = [newDesignationNodeRef, ...updatedDesignationsRef.nodes];
            return updatedDesignationsRef;
          },
        },
      });
    },
  });

  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Add Designation</Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add a Designation</DialogTitle>
        <DialogContent sx={{ minHeight: '50vh' }}>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={() => toggleDialog(false)}
            onSubmit={data => {
              createDesignation({
                variables: {
                  name: data.name,
                  departmentId: departmentId,
                },
              }).then(() => toggleDialog(false));
            }}
          >
            <FormInput
              fullWidth
              type="string"
              fieldName="name"
              label="Name"
              defaultValue=""
              validators={{
                required: true,
              }}
            />
          </FormPanel>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateDesignationDialogButton;
